import { MappedMask } from "@/store/editor/mask-utils"
import { RadialMask } from "@/core/types"
import { useEditor } from "@/utils/useEditor"
import { PropsWithChildren, useEffect, useState } from "react"
import { showingFloatingMaskingPanelAtom, updateMask } from "@/store/editor/masks-state"
import { saveCurrentAdjustmentState } from "@/store/store-history"
import { vec2 } from "gl-matrix"
import { usePointerDrag } from "@/utils/usePointerDrag"
import {
	AnchorPoint,
	anchorPoints,
	setResizeCursor,
	setRotationCursor,
	unprojectPointerCoord,
} from "@/components/canvas-controls/canvas-controls-utils"
import { CanvasControlsOverlay } from "@/components/canvas-controls/CanvasControls"
import { Ellipse } from "@/components/canvas-controls/Ellipse"
import { Handle } from "@/components/canvas-controls/Handle"
import {
	handleMaskFeather,
	handleMaskScale,
	handleMaskRotate,
} from "@/components/Masking/radial-mask-handlers"
import { useSetAtom } from "jotai"

export const RadialMaskControls = (
	props: PropsWithChildren<{ mask: MappedMask<RadialMask>; hidden: boolean }>,
) => {
	const editor = useEditor()
	const [mask, setMask] = useState(props.mask.properties)
	const setShowingFloatingMaskingPanel = useSetAtom(showingFloatingMaskingPanelAtom)

	useEffect(() => {
		setMask(props.mask.properties)
	}, [props.mask])

	const handleUpdateMask = (properties: Partial<RadialMask>) => {
		setMask({ ...mask, ...properties })
		updateMask(props.mask, properties)
	}

	const handleUpdateMaskEnd = async () => {
		await saveCurrentAdjustmentState({
			message: `Update mask`,
			changedValue: mask.name,
		})
	}

	const bindDrag = usePointerDrag<SVGElement>({
		onPointerUp: (e) => {
			e.stopPropagation()

			setShowingFloatingMaskingPanel(true)
		},
		onPointerDown: (e) => {
			e.stopPropagation()
		},
		onDrag: (drag) => {
			drag.event.stopPropagation()

			setShowingFloatingMaskingPanel(false)
			const data = drag.getData(() => {
				const anchor = drag.target?.getAttribute("data-anchor") ?? "center"

				return {
					anchor: anchor as AnchorPoint | "rotate" | "feather",
				}
			})

			const mask = props.mask.properties
			const pointerStart = unprojectPointerCoord(drag.startX, drag.startY)
			const pointer = unprojectPointerCoord(drag.x, drag.y)
			const controlPoint = anchorPoints[data.anchor] || anchorPoints.center
			const delta = vec2.sub(vec2.create(), pointer, pointerStart)

			let newMask: RadialMask

			if (data.anchor === "center") {
				// Handle mask move
				newMask = {
					...mask,
					centerX: mask.centerX + delta[0],
					centerY: mask.centerY + delta[1],
				}
			} else if (data.anchor === "rotate") {
				newMask = handleMaskRotate(mask, {
					imageSize: editor.originalImageSize,
					pointer,
					pointerStart,
				})
			} else if (data.anchor === "feather") {
				newMask = handleMaskFeather(mask, {
					pointer,
					pointerStart,
				})
			} else {
				newMask = handleMaskScale(mask, {
					imageSize: editor.originalImageSize,
					keepAspect: drag.event.shiftKey,
					fromCenter: !drag.event.altKey,
					pointer,
					pointerStart,
					controlPoint,
				})
			}

			handleUpdateMask(newMask)

			if (drag.isEnd) {
				handleUpdateMaskEnd()
			}
		},
		onPointerMove: (e) => {
			e.stopPropagation()

			const target = e.currentTarget
			const anchor = target?.getAttribute("data-anchor")

			if (anchor === "rotate") {
				setRotationCursor(e, mask.centerX, mask.centerY)
			}
		},
	})

	const { centerX, centerY, radiusX, radiusY, angle, feather } = mask

	return (
		<CanvasControlsOverlay hidden={props.hidden}>
			<Ellipse
				cx={centerX}
				cy={centerY}
				rx={radiusX}
				ry={radiusY}
				angle={angle}
				outline="none"
				stroke="transparent"
				strokeWidth={55}
				data-anchor="rotate"
				{...bindDrag}
			/>
			<Ellipse
				cx={centerX}
				cy={centerY}
				rx={radiusX * (1 - feather)}
				ry={radiusY * (1 - feather)}
				angle={angle}
				fill="none"
				stroke="white"
				strokeWidth={1}
				className="pointer-events-none"
			/>
			<Ellipse
				cx={centerX}
				cy={centerY}
				rx={radiusX}
				ry={radiusY}
				angle={angle}
				className="cursor-move"
				{...bindDrag}
			>
				<Handle x={0.5} y={0.5} anchor="center" {...bindDrag} />
				<Handle
					x={0.5}
					y={0}
					anchor="top"
					className="cursor-ns-resize"
					onPointerEnter={(e) => setRotationCursor(e, centerX, centerY)}
					{...bindDrag}
				/>
				<Handle
					x={0.5}
					y={1}
					anchor="bottom"
					className="cursor-ns-resize"
					onPointerEnter={(e) => setRotationCursor(e, centerX, centerY)}
					{...bindDrag}
				/>
				<Handle
					x={0}
					y={0.5}
					anchor="left"
					className="cursor-ew-resize"
					onPointerEnter={(e) => setResizeCursor(e, centerX, centerY)}
					{...bindDrag}
				/>
				<Handle
					x={1}
					y={0.5}
					anchor="right"
					className="cursor-ew-resize"
					onPointerEnter={(e) => setResizeCursor(e, centerX, centerY)}
					{...bindDrag}
				/>

				<Handle
					x={0.5}
					y={Math.min(0.5 + (1 - feather) / 2, 0.925)}
					data-anchor="feather"
					className="cursor-ew-resize"
					onPointerEnter={(e) => setResizeCursor(e, centerX, centerY)}
					{...bindDrag}
				/>
			</Ellipse>
			{props.children}
		</CanvasControlsOverlay>
	)
}
